import { getLabel, getLabelCapco, type LabelValue } from '@/utils/label';
import { DEFAULT_MARKING } from './marking';

/**
 * Which marking a displayed text carries. Three rules, three functions, named
 * so that the assumption each makes is in its name:
 *
 *  - `labelMarking`: a model's, task's or resource's name or description. A
 *    text with no stored marking is ASSUMED `U` (user ruling, 2026-10-12).
 *  - `storedLabelMarking`: a functional's, jar's or folder's name. Only a
 *    marking that is actually stored is shown; nothing is assumed.
 *  - `documentNameMarking`: whatever string is shown as a document's name
 *    (its filename first). It carries the name's marking, the label's.
 *
 * None of them lowers or rewrites a stored marking: `S//NF` comes back
 * `S//NF`.
 */

/**
 * A label's text, as `getLabel` reads it, except that a label object with no
 * string `value` has NO text (`getLabel` renders it `[object Object]`). A
 * place then shows its fallback, never a marked `[object Object]`.
 */
export function labelText(label: LabelValue): string {
  if (label && typeof label === 'object' && typeof (label as { value?: unknown }).value !== 'string') return '';
  return getLabel(label);
}

/** The marking stored on a label, or `''`. A blank or whitespace-only one is absent. */
function stored(label: LabelValue): string {
  const capco = getLabelCapco(label);
  return typeof capco === 'string' && capco.trim() ? capco : '';
}

/**
 * The marking to display beside a model's, task's or resource's text: the
 * label's own `capco`, exactly as stored, and `U` (`DEFAULT_MARKING`) when
 * the label has text and no stored marking. That is what the edit control
 * (`MarkingControl`) opens the same label at, and what the server reads an
 * absent marking as. It covers a plain string label too.
 *
 * A label with NO text has no marking to show: `''`. A call site's fallback
 * (an id, "Untitled Model") is not a marked text and gets no mark.
 */
export function labelMarking(label: LabelValue): string {
  if (!labelText(label)) return '';
  return stored(label) || DEFAULT_MARKING;
}

/**
 * The marking to display beside a text ONLY when one is stored: `''` for a
 * plain string label and for a label object with no `capco`. For functional
 * and jar definitions and for folders, where a missing marking is NOT assumed
 * to be `U` (user ruling, 2026-10-12).
 */
export function storedLabelMarking(label: LabelValue): string {
  if (!labelText(label)) return '';
  return stored(label);
}

/**
 * The marking of the string shown as a DOCUMENT's name. A document is shown
 * by its `filename` first, and the upload form stores the marking the user
 * chose for the filename on the label, so the filename carries the label's
 * marking wherever it stands for the document's name, whether or not it is
 * still the label's text. A document with a name is never shown without a
 * mark while CAPCO is on: one with no stored marking is assumed `U`.
 *
 * `''` only for a document with NO name at all (no filename and no label
 * text): the place then shows its id, and an id is not a marked text.
 */
export function documentNameMarking(
  doc: { filename?: string; fileName?: string; metadata?: { label?: LabelValue } } | null | undefined, // NOSONAR typescript:S4782 published-contract: LabelValue is the shared label type and includes undefined; ? marks a label key the document metadata may omit
): string {
  if (!doc?.filename && !doc?.fileName && !labelText(doc?.metadata?.label)) return '';
  return stored(doc?.metadata?.label) || DEFAULT_MARKING;
}
